import type { IconName } from "@/components/Icon/iconMap";
import type { ApiDeepRequiredResponseBody } from "@/lib/api/api-type-helper";
import type { EnumUnion } from "@dresscode-frontend/utility-types";
import { match } from "ts-pattern";

export const DeviceType = {
  COMPUTER: "COMPUTER",
  MOBILE: "MOBILE",
  MONITOR: "MONITOR",
  SERVER: "SERVER",
  OTHER: "OTHER",
} as const;
export type DeviceType = EnumUnion<typeof DeviceType>;

export const DeviceComputerType = {
  LAPTOP: "LAPTOP",
  WORKSTATION: "WORKSTATION",
  MOBILE: "MOBILE",
  TABLET: "TABLET",
} as const;
export type DeviceComputerType = EnumUnion<typeof DeviceComputerType>;

export const DeviceGenre = {
  LAPTOP: "LAPTOP",
  WORKSTATION: "WORKSTATION",
  MONITOR: "MONITOR",
  MOBILE: "MOBILE",
  TABLET: "TABLET",
  SERVER: "SERVER",
  OTHER: "OTHER",
} as const;

export type DeviceGenre = (typeof DeviceGenre)[keyof typeof DeviceGenre];

export const DeviceGenreTranslationKey = {
  [DeviceGenre.MOBILE]: "mobile",
  [DeviceGenre.MONITOR]: "monitor",
  [DeviceGenre.OTHER]: "other",
  [DeviceGenre.WORKSTATION]: "workstation",
  [DeviceGenre.LAPTOP]: "laptop",
  [DeviceGenre.TABLET]: "tablet",
  [DeviceGenre.SERVER]: "server",
} as const;

export type DeviceGenreTranslationKey =
  (typeof DeviceGenreTranslationKey)[DeviceGenre];

export const getDeviceGenreTranslationKey = (
  genre: DeviceGenre,
): DeviceGenreTranslationKey =>
  match(genre)
    .with(
      DeviceGenre.MOBILE,
      () => DeviceGenreTranslationKey[DeviceGenre.MOBILE],
    )
    .with(
      DeviceGenre.MONITOR,
      () => DeviceGenreTranslationKey[DeviceGenre.MONITOR],
    )
    .with(DeviceGenre.OTHER, () => DeviceGenreTranslationKey[DeviceGenre.OTHER])
    .with(
      DeviceGenre.WORKSTATION,
      () => DeviceGenreTranslationKey[DeviceGenre.WORKSTATION],
    )
    .with(
      DeviceGenre.LAPTOP,
      () => DeviceGenreTranslationKey[DeviceGenre.LAPTOP],
    )
    .with(
      DeviceGenre.TABLET,
      () => DeviceGenreTranslationKey[DeviceGenre.TABLET],
    )
    .with(
      DeviceGenre.SERVER,
      () => DeviceGenreTranslationKey[DeviceGenre.SERVER],
    )
    .exhaustive();

export const OSPlatformType = {
  WINDOWS: "WINDOWS",
  MACOS: "MACOS",
  LINUX: "LINUX",
  IOS: "IOS",
  ANDROID: "ANDROID",
  TVOS: "TVOS",
  WATCHOS: "WATCHOS",
  VISIONOS: "VISIONOS",
  OTHER: "OTHER",
} as const satisfies Record<
  string,
  ApiDeepRequiredResponseBody<
    "/v2/devices/{id}",
    "get"
  >["computer"]["osPlatform"]
>;
export type OSPlatformType = EnumUnion<typeof OSPlatformType>;

export const getOSPlatformIcon = (osPlatform?: OSPlatformType): IconName =>
  match<OSPlatformType | undefined, IconName>(osPlatform)
    .with("WINDOWS", () => "OsWindowsIcon")
    .with("LINUX", () => "OsLinuxIcon")
    .with("ANDROID", () => "OsAndroidIcon")
    .with("MACOS", () => "OsMacosIcon")
    .with("IOS", () => "OsMacosIcon")
    .with("TVOS", () => "OsMacosIcon")
    .with("WATCHOS", () => "OsMacosIcon")
    .with("VISIONOS", () => "OsMacosIcon")
    .otherwise(() => "OsOthersIcon");

export const PortType = {
  USB_TYPE_A: "USB_TYPE_A",
  USB_TYPE_B: "USB_TYPE_B",
  USB_TYPE_C: "USB_TYPE_C",
  THUNDERBOLT: "THUNDERBOLT",
  MINI_USB: "MINI_USB",
  MICRO_USB: "MICRO_USB",
  USB_MICRO_B: "USB_MICRO_B",
  HDMI: "HDMI",
  DISPLAYPORT: "DISPLAYPORT",
  MINI_DISPLAYPORT: "MINI_DISPLAYPORT",
  VGA: "VGA",
  DVI: "DVI",
  AUDIO: "AUDIO",
  LAN: "LAN",
} as const satisfies Record<
  string,
  ApiDeepRequiredResponseBody<
    "/v2/devices/{id}",
    "get"
  >["ports"][number]["type"]
>;
export type PortType = EnumUnion<typeof PortType>;

export const PortTypeLabel = {
  USB_TYPE_A: "USB Type A",
  USB_TYPE_B: "USB Type B",
  USB_TYPE_C: "USB Type C",
  THUNDERBOLT: "Thunderbolt",
  MINI_USB: "Mini USB",
  MICRO_USB: "Micro USB",
  USB_MICRO_B: "USB Micro B",
  HDMI: "HDMI",
  DISPLAYPORT: "DisplayPort",
  MINI_DISPLAYPORT: "Mini DisplayPort",
  VGA: "VGA",
  DVI: "DVI",
  AUDIO: "Audio",
  LAN: "LAN",
} as const satisfies Record<PortType, string>;

export const NetworkInterfaceType = {
  UNKNOWN: "UNKNOWN",
  ETHERNET: "ETHERNET",
  IEEE_802_11: "IEEE_802_11",
  IEEE_802_15_1: "IEEE_802_15_1",
} as const satisfies Record<
  string,
  ApiDeepRequiredResponseBody<
    "/v2/devices/{id}",
    "get"
  >["networkInterfaces"][number]["type"]
>;
export type NetworkInterfaceType = EnumUnion<typeof NetworkInterfaceType>;

export const NetworkInterfaceTypeLabel = {
  UNKNOWN: "Unknown",
  ETHERNET: "Ethernet",
  IEEE_802_11: "Wi-Fi",
  IEEE_802_15_1: "Bluetooth",
} as const satisfies Record<NetworkInterfaceType, string>;

export const StorageType = {
  SSD: "SSD",
  HDD: "HDD",
  UNKNOWN: "UNKNOWN",
} as const satisfies Record<
  string,
  ApiDeepRequiredResponseBody<
    "/v2/devices/{id}",
    "get"
  >["storages"][number]["type"]
>;
export type StorageType = EnumUnion<typeof StorageType>;

export const StorageTypeLabel = {
  SSD: "SSD",
  HDD: "HDD",
  UNKNOWN: "Unknown",
} as const satisfies Record<StorageType, string>;

export const PurchaseCondition = {
  NEW: "NEW",
  USED: "USED",
} as const;
export type PurchaseCondition = EnumUnion<typeof PurchaseCondition>;

export const ProcurementType = {
  PURCHASE: "PURCHASE",
  LEASE: "LEASE",
  RENTAL: "RENTAL",
  OTHER: "OTHER",
} as const;
export type ProcurementType = EnumUnion<typeof ProcurementType>;

export const PlacementType = {
  FIXED: "FIXED",
  FREE: "FREE",
  STOCK: "STOCK",
  OTHER: "OTHER",
  UNKNOWN: "UNKNOWN",
} as const;
export type PlacementType = EnumUnion<typeof PlacementType>;

//########################################################
// Device Activity Type
// （こういう系、featuresに移すのとどちらがいいのか悩み中）
//########################################################
export const DeviceActivityType = {
  ORDERED: "ORDERED",
  PROCURED: "PROCURED",
  DECOMMISSIONED: "DECOMMISSIONED",
  TROUBLED: "TROUBLED",
  RECOVERED: "RECOVERED",
  DELETED: "DELETED",
} as const;

export type DeviceActivityType = EnumUnion<typeof DeviceActivityType>;

export const getDeviceActivityIcon = (type: DeviceActivityType): IconName =>
  match(type)
    .with("ORDERED", () => "PackagePlus" as const)
    .with("PROCURED", () => "PackagePlus" as const)
    .with("DECOMMISSIONED", () => "Trash2Icon" as const)
    .with("TROUBLED", () => "HeartMinusIcon" as const)
    .with("RECOVERED", () => "HeartPulseIcon" as const)
    .with("DELETED", () => "Trash2Icon" as const)
    .exhaustive();

//########################################################
// Network And SIM Type
//########################################################
export type NetworkAndSimType = {
  mobileCarrier: string | null;
  simStatus: string | null;
  iccid: string | null;
  phoneNumber: string | null;
  phoneCountryCode: string | null;
  networkStatus: string | null;
  connectedWifiSsid: string | null;
  ipAddress: string | null;
};

//########################################################
// Currency Code
//########################################################
// デバイスの調達に使用する通貨コードの定義
export type CurrencyCode = ApiDeepRequiredResponseBody<
  "/v2/devices/{id}",
  "get"
>["purchasePrice"]["currency"];

export const CURRENCY_CODES = [
  "JPY",
  "USD",
  "IDR",
  "THB",
  "VND",
  "CNY",
] as const satisfies readonly CurrencyCode[];
